/**
 * useMeFeatureFlags — feature-flag access for the /me couple surface (WP-1).
 *
 * Wraps the orval-generated flags client (GET /me/feature-flags) the same way
 * the therapist portal's `useFeatureFlags` wraps its endpoint: flags default
 * to OFF until the backend resolves, so callers can branch on a boolean with
 * no loading/null guards, and an error degrades to the dark (off) default —
 * never a throw. The backend already returns the safe default for unseeded
 * flags; this default is the belt-and-braces guard for the loading/error
 * window.
 *
 * Person-token gating: the query is INTERNALLY DISABLED unless the held token
 * is person-bound. A pre-bind couple token calling this person-only endpoint
 * would 401, and `customInstance` wipes the whole session on 401 — the hook
 * must never log the pre-bind opener out of the picker.
 *
 * Provider-safe by design: onboarding step components are unit-rendered
 * without MeAuthProvider/QueryClientProvider (e.g. the S11 suite), and S11
 * now reads this hook. Outside MeAuthProvider we treat the session as
 * unauthed; outside QueryClientProvider the query is force-DISABLED (the
 * `enabled` gate requires a context client), so the detached client below
 * only ever satisfies useQuery's client resolution — it can never fetch,
 * even if a person-bound token happens to sit in localStorage.
 */

import { useContext } from "react";
import {
  QueryClient,
  QueryClientContext,
  useQuery,
} from "@tanstack/react-query";
import { getMeFeatureFlags } from "@/__generated__/api/me/me";
import type { MeFeatureFlagsOut } from "@/__generated__/api/wrenAPI.schemas";
import { getClientSessionToken } from "@/api/client";
import { getMeTokenBinding, type MeTokenBinding } from "../auth/meToken";
import { useMeAuth } from "../auth/MeAuthContext";

export interface MeFeatureFlags {
  homebase: boolean;
  /** Embedded Whereby call inside /me (vs window.open). Dark by default. */
  homebase_embedded_call: boolean;
}

/** Flags are off until the backend says otherwise. */
export const DEFAULT_ME_FEATURE_FLAGS: MeFeatureFlags = {
  homebase: false,
  homebase_embedded_call: false,
};

/** Locked query key (contracts §4.4 key map — cross-WP invalidation). */
export const ME_FEATURE_FLAGS_QUERY_KEY = ["me", "feature-flags"] as const;

/** Flags are stable per-session; 5 min between background refetches. */
const ME_FEATURE_FLAGS_STALE_TIME_MS = 5 * 60 * 1000;

/** Shared React Query config — reactive hook and imperative
 *  `ensureQueryData` callers hit the same cache entry. */
export function meFeatureFlagsQueryOptions() {
  return {
    queryKey: ME_FEATURE_FLAGS_QUERY_KEY,
    queryFn: async (): Promise<MeFeatureFlags> => {
      const res = await getMeFeatureFlags();
      const wire = res.data as MeFeatureFlagsOut;
      return {
        homebase: !!wire?.homebase,
        homebase_embedded_call: !!wire?.homebase_embedded_call,
      };
    },
    staleTime: ME_FEATURE_FLAGS_STALE_TIME_MS,
  };
}

/**
 * The held token's binding, tolerant of provider-less renders: inside
 * MeAuthProvider this is the live context value; outside (unit-rendered step
 * components) fall back to decoding the stored token directly.
 */
function useMeAuthBindingSafe(): MeTokenBinding {
  try {
    // Hook-order-stable ONLY because useMeAuth throws AFTER its single
    // useContext call (see the invariant note on useMeAuth itself) — the
    // same hooks run on every render whether or not the provider exists.
    return useMeAuth().binding;
  } catch {
    return getMeTokenBinding(getClientSessionToken());
  }
}

/** Parked client for provider-less renders — the query is disabled there, so
 *  this client never fetches; it only satisfies useQuery's client resolution. */
const detachedQueryClient = new QueryClient();

export interface UseMeFeatureFlagsResult {
  flags: MeFeatureFlags;
  /** True until the flags query first settles (success or error) — lets the
   *  route gate hold (SectionLoading) rather than branch on the dark default. */
  isLoading: boolean;
}

export function useMeFeatureFlags(): UseMeFeatureFlagsResult {
  const binding = useMeAuthBindingSafe();
  const contextClient = useContext(QueryClientContext);
  // Both gates required: person-bound token (a couple token would 401 →
  // customInstance wipes the session) AND a real provider client (the
  // detached fallback must never fetch into a cache the app can't see).
  const enabled = binding === "person" && contextClient != null;
  const query = useQuery(
    { ...meFeatureFlagsQueryOptions(), enabled },
    contextClient ?? detachedQueryClient
  );
  return {
    flags: query.data ?? DEFAULT_ME_FEATURE_FLAGS,
    isLoading: enabled && query.isLoading,
  };
}
